import i18n from 'i18next';

import { sharedTypes } from '@/shared/Constants';

import type { UnknownAction } from 'redux';
import type { UserData, UserSettings } from '@/shared/types';

interface SharedState {
	loading: boolean;
	userData: Partial<UserData>;
	triggerMessagePresent: boolean;
	translationsInstance: typeof i18n.t;
	backgroundColor: string | null;
	backgroundPattern: string | null;
	openMobileFooterMenu: boolean;
	openGeneralFeedbackForm: boolean;
}

interface UserReward {
	visible?: boolean;
	background_color?: string | null;
	background_pattern?: string | null;
}

interface SharedActionPayload {
	backgroundColor?: string | null;
	backgroundPattern?: string | null;
	data?: Partial<SharedState>;
	translationsInstance?: SharedState['translationsInstance'];
	userData?: Partial<UserData>;
	openMobileFooterMenu?: boolean;
	openGeneralFeedbackForm?: boolean;
	settings?: UserSettings;
}

const initialState: SharedState = {
	loading: false,
	userData: {
		bridge_game: {},
	},
	triggerMessagePresent: false,
	translationsInstance: i18n.t,
	backgroundColor: null,
	backgroundPattern: null,
	openMobileFooterMenu: false,
	openGeneralFeedbackForm: false,
};

const prefillPasswordSettings = (userData?: Partial<UserData> | null): UserSettings | undefined => {
	const { sidk_user, has_default_goal, settings } = userData || {};

	if (!settings) {
		return settings;
	}

	if (!sidk_user && !has_default_goal) {
		return settings;
	}

	return {
		...settings,
		password: {
			...(settings?.password ?? {}),
			...(sidk_user
				? {
						view_activity_page: false,
						view_progress_page: false,
				  }
				: {}),
			...(has_default_goal
				? {
						update_learning_goals: false,
				  }
				: {}),
		} as UserSettings['password'],
	};
};

export default function sharedReducer(
	state: SharedState = initialState,
	action: UnknownAction,
): SharedState {
	const { type, payload } = action;
	const typedPayload = (payload ?? {}) as SharedActionPayload;

	switch (type) {
		case sharedTypes.GET_USER_DATA_REQUEST:
			return {
				...state,
				loading: true,
			};

		case sharedTypes.GET_USER_DATA_SUCCESS:
			const reward = (typedPayload.userData?.reward ?? {}) as UserReward;
			const nextUserData: Partial<UserData> = {
				// TODO: remove bridge_game fallback — will come from the API
				bridge_game: initialState.userData.bridge_game,
				...(typedPayload.userData ?? {}),
				settings: prefillPasswordSettings(typedPayload.userData),
			};

			return {
				...state,
				userData: nextUserData,
				backgroundColor: reward?.visible ? reward?.background_color ?? null : null,
				backgroundPattern: reward?.visible ? reward?.background_pattern ?? null : null,
				loading: false,
			};

		case sharedTypes.GET_USER_DATA_ERROR:
			return {
				...state,
				loading: false,
			};

		case sharedTypes.CLEAR_USER_DATA:
			return {
				...state,
				userData: initialState.userData,
			};

		case sharedTypes.SEND_EVENT_SUCCESS:
			return {
				...state,
				...(typedPayload.data ?? {}),
			};

		case sharedTypes.SEND_EVENT_ERROR:
			return {
				...state,
			};

		case sharedTypes.SET_TRANSLATIONS_INSTANCE:
			return {
				...state,
				translationsInstance:
					typedPayload.translationsInstance ?? state.translationsInstance,
			};

		case sharedTypes.SET_BACKGROUND_COLOR:
			return {
				...state,
				backgroundColor: typedPayload.backgroundColor ?? null,
			};

		case sharedTypes.SET_BACKGROUND_PATTERN:
			return {
				...state,
				backgroundPattern: typedPayload.backgroundPattern ?? null,
			};

		case sharedTypes.TOGGLE_MOBILE_FOOTER_MENU:
			return {
				...state,
				openMobileFooterMenu:
					typedPayload.openMobileFooterMenu ?? state.openMobileFooterMenu,
			};

		case sharedTypes.TOGGLE_GENERAL_FEEDBACK_FORM:
			return {
				...state,
				openGeneralFeedbackForm:
					typedPayload.openGeneralFeedbackForm ?? state.openGeneralFeedbackForm,
			};

		case sharedTypes.UPDATE_USER_DATA_SETTINGS:
			return {
				...state,
				userData: {
					...state.userData,
					settings: typedPayload.settings,
				},
			};

		default:
			return state;
	}
}
